/**
 * Dashboard home — Ambient Notes action centre.
 *
 * Two big capture tiles (field / meeting), recent sessions list, and tip
 * row once the user has some sessions under their belt.
 */
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Mic,
  Wrench,
  Users,
  ArrowRight,
  Clock,
  History,
  Loader2,
  Flag,
  CheckCircle2,
  Sparkles,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useSession } from '@/client/lib/auth'
import { apiClient } from '@/client/lib/api-client'
import { cn } from '@/lib/utils'

interface SessionRow {
  id: string
  mode: 'field' | 'meeting'
  title: string | null
  status: 'recording' | 'processing' | 'done' | 'failed'
  startedAt: string | number
  endedAt: string | number | null
}

function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function firstName(name?: string | null): string {
  if (!name) return ''
  const first = name.split(' ')[0] ?? ''
  return first ? `, ${first}` : ''
}

function formatWhen(startedAt: string | number): string {
  const d = new Date(startedAt)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function formatDuration(startedAt: string | number, endedAt: string | number | null): string {
  if (!endedAt) return 'ongoing'
  const mins = Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 60000)
  if (mins < 1) return '< 1 min'
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

export function DashboardPage() {
  const { data: auth } = useSession()
  const { data, isLoading } = useQuery<{ sessions: SessionRow[] }>({
    queryKey: ['sessions', 'dashboard'],
    queryFn: () => apiClient.get('/api/sessions?limit=5'),
  })

  const sessions = data?.sessions ?? []
  const greeting = greetingForHour(new Date().getHours())
  const inProgress = sessions.find((s) => s.status === 'recording' || s.status === 'processing')

  return (
    <div className="flex flex-col gap-8">
      {/* Welcome */}
      <div className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
          {greeting}{firstName(auth?.user?.name)}
        </h1>
        <p className="text-sm text-muted-foreground">
          Capture a job or a meeting. Transcripts, flagged moments, and structured outputs follow.
        </p>
      </div>

      {/* Resume banner if something is mid-flight */}
      {inProgress && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="py-4 flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Session in progress</p>
              <p className="text-xs text-muted-foreground">
                {inProgress.mode === 'field' ? 'Field job' : 'Meeting'} · started {formatWhen(inProgress.startedAt)}
              </p>
            </div>
            <Button asChild size="sm" variant="secondary">
              <Link to={`/dashboard/sessions/${inProgress.id}`}>
                Open
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Big capture tiles */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Link to="/dashboard/record?mode=field" className="group">
          <Card className="h-full border-primary/20 transition-all group-hover:border-primary group-hover:shadow-md">
            <CardContent className="py-6 px-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Wrench className="h-6 w-6" />
                </div>
                <Mic className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <h2 className="text-xl font-semibold mb-1">Record a field job</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Narrate as you work. Steps and flagged moments populate live.
              </p>
              <div className="flex items-center gap-2 text-sm text-primary font-medium">
                Start recording
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/record?mode=meeting" className="group">
          <Card className="h-full border-primary/20 transition-all group-hover:border-primary group-hover:shadow-md">
            <CardContent className="py-6 px-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Users className="h-6 w-6" />
                </div>
                <Mic className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <h2 className="text-xl font-semibold mb-1">Capture a meeting</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Decisions, action items, and talking points as the conversation unfolds.
              </p>
              <div className="flex items-center gap-2 text-sm text-primary font-medium">
                Start recording
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent sessions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            Recent sessions
          </h2>
          {sessions.length > 0 && (
            <Button asChild variant="ghost" size="sm">
              <Link to="/dashboard/sessions">
                View all
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          )}
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="py-6 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : sessions.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted mb-3">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No sessions yet</p>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                Your first session will appear here. Tap a capture tile above to start.
              </p>
              <Button asChild size="sm">
                <Link to="/dashboard/record">
                  <Mic className="mr-2 h-3.5 w-3.5" />
                  Record your first session
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.id}>
                <Link to={`/dashboard/sessions/${s.id}`} className="block">
                  <Card className="hover:border-primary/40 transition-colors">
                    <CardContent className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg shrink-0',
                          s.mode === 'field'
                            ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                            : 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
                        )}>
                          {s.mode === 'field' ? <Wrench className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium truncate">
                              {s.title ?? `${s.mode === 'field' ? 'Field job' : 'Meeting'} ${s.id.slice(0, 8)}`}
                            </span>
                            {s.status !== 'done' && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                {s.status}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            {formatWhen(s.startedAt)} · {formatDuration(s.startedAt, s.endedAt)}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tip row — subtle guidance once the user has at least one session */}
      {sessions.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3 text-sm">
          <div className="flex items-start gap-2.5 text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>
              <span className="text-foreground font-medium">Narrate out loud.</span>{' '}
              Short, natural phrases. The app chunks every 60 seconds.
            </span>
          </div>
          <div className="flex items-start gap-2.5 text-muted-foreground">
            <Flag className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
            <span>
              <span className="text-foreground font-medium">Flag tricky bits.</span>{' '}
              Say &ldquo;that&apos;s not right&rdquo; or call out part numbers; they surface as flagged events.
            </span>
          </div>
          <div className="flex items-start gap-2.5 text-muted-foreground">
            <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>
              <span className="text-foreground font-medium">Pick a template after.</span>{' '}
              Transcript plus events become an invoice snippet, training doc, or meeting minutes.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
